import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Bell, Check, CheckCheck, Mail, MessageSquare } from "lucide-react";

const KIND_STYLE = {
  application: "cad-chip-blue",
  approval: "cad-chip-green",
  payment: "cad-chip-gold",
  inspection: "cad-chip-blue",
  certificate: "cad-chip-green",
  appeal: "cad-chip-red",
  renewal: "cad-chip-gold",
  expiry: "cad-chip-red",
  compliance: "cad-chip-gold",
  conflict: "cad-chip-red",
};

const CH_ICON = { email: Mail, sms: MessageSquare, whatsapp: MessageSquare, in_app: Bell, push: Bell };

export const Notifications = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["notifs"], queryFn: async () => (await api.get("/notifications")).data });

  const readOne = useMutation({
    mutationFn: async (id) => (await api.post(`/notifications/${id}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifs"] }),
  });
  const readAll = useMutation({
    mutationFn: async () => (await api.post("/notifications/read-all")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifs"] }),
  });

  const items = data?.items || [];
  const unread = data?.unread || 0;

  return (
    <div className="space-y-6" data-testid="page-notifications">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Enterprise Notification Centre</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
            <Bell className="text-[#C99A2E]"/> Notifications
            {unread > 0 && <span className="cad-chip cad-chip-gold">{unread} unread</span>}
          </h1>
          <div className="text-[13px] text-[#B3C2CC] mt-1">In-app · Email · SMS · WhatsApp · Push — configurable across applications, approvals, payments, inspections and expiry alerts.</div>
        </div>
        <button onClick={() => readAll.mutate()} data-testid="notif-read-all"
                className="cad-btn-outline px-3 py-2 rounded-sm text-[13px] inline-flex items-center gap-1.5">
          <CheckCheck size={14}/> Mark all read
        </button>
      </header>

      <div className="cad-panel overflow-hidden">
        <ul className="divide-y" style={{ borderColor: "rgba(65,83,99,0.4)" }}>
          {items.map((n) => {
            const Ch = CH_ICON[n.channel] || Bell;
            return (
              <li key={n.id} data-testid={`notif-${n.id}`}
                  className={`px-5 py-4 flex items-start gap-4 ${n.read ? "" : "bg-[#0B3558]/40"}`}>
                <Ch size={18} className="text-[#C99A2E] mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`cad-chip ${KIND_STYLE[n.kind] || ""}`}>{n.kind}</span>
                    <span className="cad-chip">{n.channel}</span>
                    {!n.read && <span className="cad-chip cad-chip-gold">NEW</span>}
                    <span className="ml-auto font-mono-cad text-[10px] text-[#7C93A5]">{n.created_at.slice(0, 16).replace("T"," ")}</span>
                  </div>
                  <div className="font-display font-bold text-[#F6F8FA] mt-1.5">{n.title}</div>
                  <div className="text-[13px] text-[#B3C2CC] mt-1">{n.body}</div>
                  {n.reference && <div className="text-[11px] font-mono-cad text-[#7CBFDC] mt-1">{n.reference}</div>}
                </div>
                {!n.read && (
                  <button onClick={() => readOne.mutate(n.id)} data-testid={`notif-read-${n.id}`}
                          className="cad-btn-outline px-2 py-1 rounded-sm text-[11px] inline-flex items-center gap-1"><Check size={11}/> Read</button>
                )}
              </li>
            );
          })}
          {items.length === 0 && <li className="px-5 py-10 text-center text-[#7C93A5] italic">No notifications.</li>}
        </ul>
      </div>
    </div>
  );
};
